import create from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { fetchJson } from "@/api";
import { computeAvailablePaymentShipment, } from "@/components/cart/utils";
import { computePriceWithDiscount, getCsfrToken, roundPrice } from "@/utils";
import { useGlobalStore } from "./global";
const CART_COUNT = '#mobile-app-navbar .count, #mainNav .count';
export var Step;
(function (Step) {
    Step[Step["SUMMARY"] = 0] = "SUMMARY";
    Step[Step["ORDER"] = 1] = "ORDER";
})(Step || (Step = {}));
export const useCartStore = create()(devtools(immer((set) => ({
    step: Step.SUMMARY,
    cart: {},
    totalItemsWeight: 0,
    totalItemsPrice: 0,
    couponDiscount: 0,
    totalItemsPriceWithDiscount: 0,
    totalShipmentPrice: 0,
    totalPaymentPrice: 0,
    activeShipmentTresholdDifference: "skip",
    availablePaymentMethods: [],
    activeServices: [],
    availableGifts: [],
    invoiceCountry: '',
    availableDifferentDeliveryAddress: true,
    isCompany: false,
    newAccount: false,
    differentDeliveryAddress: false,
    newsletter: false,
    acceptedConditions: false,
    setStep: (step) => set({ step }),
    addItemQuantity: (id, value) => {
        set((state) => {
            const item = state.cart.items.find((item) => item.id === id);
            if (item)
                item.quantity += value;
            state.totalItemsWeight = computeItemsWeight(state);
            state.totalItemsPrice = computeItemsPrice(state);
            state.totalItemsPriceWithDiscount = computeItemsPriceWithDiscount(state);
            state.availablePaymentMethods =
                computeAvailablePaymentShipment(state);
            if (state.availablePaymentMethods[0]) {
                state.activePayment = state.availablePaymentMethods[0];
                state.activeShipment =
                    state.activePayment?.shipments[0];
                state.totalPaymentPrice = computePaymentPrice(state);
                state.totalShipmentPrice = computeShipmentPrice(state);
                state.activeShipmentTresholdDifference =
                    computeActiveShipmentTresholdDifference(state);
            }
        });
    },
    setItemQuantity: (id, value) => {
        set((state) => {
            const item = state.cart.items.find((item) => item.id === id);
            if (item)
                item.quantity = value;
            state.totalItemsWeight = computeItemsWeight(state);
            state.totalItemsPrice = computeItemsPrice(state);
            state.totalItemsPriceWithDiscount =
                computeItemsPriceWithDiscount(state);
            state.availablePaymentMethods =
                computeAvailablePaymentShipment(state);
            if (state.availablePaymentMethods[0]) {
                state.activePayment = state.availablePaymentMethods[0];
                state.activeShipment =
                    state.activePayment?.shipments[0];
                state.totalPaymentPrice = computePaymentPrice(state);
                state.totalShipmentPrice = computeShipmentPrice(state);
                state.activeShipmentTresholdDifference =
                    computeActiveShipmentTresholdDifference(state);
            }
        });
    },
    removeItem: (id) => {
        set((state) => {
            state.cart.items = state.cart.items.filter((item) => item.id !== id);
            state.totalItemsWeight = computeItemsWeight(state);
            state.totalItemsPrice = computeItemsPrice(state);
            state.totalItemsPriceWithDiscount =
                computeItemsPriceWithDiscount(state);
            state.availablePaymentMethods =
                computeAvailablePaymentShipment(state);
            if (state.availablePaymentMethods[0]) {
                state.activePayment = state.availablePaymentMethods[0];
                state.activeShipment =
                    state.activePayment?.shipments[0];
                state.totalPaymentPrice = computePaymentPrice(state);
                state.totalShipmentPrice = computeShipmentPrice(state);
                state.activeShipmentTresholdDifference =
                    computeActiveShipmentTresholdDifference(state);
            }
        });
    },
    setActivePayment: (payment) => {
        set((state) => {
            if (!payment)
                return;
            state.activePayment = payment;
            state.activeShipment = state.activePayment?.shipments[0];
            state.totalPaymentPrice = computePaymentPrice(state);
            state.totalShipmentPrice = computeShipmentPrice(state);
            state.activeShipmentTresholdDifference =
                computeActiveShipmentTresholdDifference(state);
        });
    },
    setActiveShipment: (shipment) => {
        set((state) => {
            if (!shipment)
                return;
            state.activeShipment = shipment;
            state.totalShipmentPrice = computeShipmentPrice(state);
            state.activeShipmentTresholdDifference =
                computeActiveShipmentTresholdDifference(state);
            const settings = useGlobalStore.getState().config.template_settings.cart;
            state.availableDifferentDeliveryAddress = !shipment.is_delivery_place_shipment ||
                (shipment.is_delivery_place_shipment && settings.show_delivery_address_on_delivery_place_shipment.enabled);
            if (!state.activeShipment.is_delivery_place_shipment) {
                state.activePlaceOfDelivery = undefined;
                state.deliveryTime = undefined;
            }
        });
    },
    setCouponDiscount: (couponDiscount) => set((state) => {
        state.couponDiscount = couponDiscount;
        state.totalItemsPrice = computeItemsPrice(state);
        state.totalItemsPriceWithDiscount =
            computeItemsPriceWithDiscount(state);
        state.availablePaymentMethods =
            computeAvailablePaymentShipment(state);
        if (state.availablePaymentMethods[0]) {
            state.activePayment = state.availablePaymentMethods[0];
            state.activeShipment =
                state.activePayment?.shipments[0];
            state.totalPaymentPrice = computePaymentPrice(state);
            state.totalShipmentPrice = computeShipmentPrice(state);
            state.activeShipmentTresholdDifference =
                computeActiveShipmentTresholdDifference(state);
        }
    }),
    changeService: (itemId, id, value) => {
        set((state) => {
            const services = state.activeServices.find((item) => item.itemId === itemId)?.serviceIds;
            if (!services && value) {
                state.activeServices.push({
                    itemId,
                    serviceIds: [id],
                });
            }
            else if (services) {
                const index = services.indexOf(id);
                if (index > -1 && !value) {
                    services.splice(index, 1);
                }
                else if (index === -1 && value) {
                    services.push(id);
                }
            }
            state.totalItemsPrice = computeItemsPrice(state);
            state.totalItemsPriceWithDiscount =
                computeItemsPriceWithDiscount(state);
            state.availablePaymentMethods =
                computeAvailablePaymentShipment(state);
            if (state.availablePaymentMethods[0]) {
                state.activePayment = state.availablePaymentMethods[0];
                state.activeShipment =
                    state.activePayment?.shipments[0];
                state.totalPaymentPrice = computePaymentPrice(state);
                state.totalShipmentPrice = computeShipmentPrice(state);
                state.activeShipmentTresholdDifference =
                    computeActiveShipmentTresholdDifference(state);
            }
        });
    },
    setActiveGift: (gift) => {
        set((state) => {
            state.activeGift = gift;
        });
    },
    toggleCompany: () => {
        set((state) => {
            state.isCompany = !state.isCompany;
        });
    },
    toggleNewAccount: () => {
        set((state) => {
            state.newAccount = !state.newAccount;
        });
    },
    toggleDifferentDeliveryAddress: () => {
        set((state) => {
            state.differentDeliveryAddress =
                !state.differentDeliveryAddress;
        });
    },
    toggleNewsletter: () => {
        set((state) => {
            state.newsletter = !state.newsletter;
        });
    },
    toggleAcceptedConditions: () => {
        set((state) => {
            state.acceptedConditions = !state.acceptedConditions;
        });
    },
    setInvoiceCountry: (country) => {
        set(state => {
            state.invoiceCountry = country;
        });
    },
    setActiveDeliveryPlace: (placeOfDelivery) => {
        set((state) => {
            if (!placeOfDelivery) {
                return;
            }
            state.activePlaceOfDelivery = placeOfDelivery;
        });
    },
    setDeliveryTime: (time) => {
        set((state) => {
            if (!time) {
                return;
            }
            state.deliveryTime = time;
        });
    },
    set: (cart) => {
        set((state) => {
            state.cart = cart;
            state.totalItemsWeight = computeItemsWeight(state);
            state.totalItemsPrice = computeItemsPrice(state);
            state.totalItemsPriceWithDiscount =
                computeItemsPriceWithDiscount(state);
            state.availablePaymentMethods =
                computeAvailablePaymentShipment(state);
            if (state.availablePaymentMethods[0]) {
                state.activePayment = state.availablePaymentMethods[0];
                state.activeShipment =
                    state.activePayment?.shipments[0];
                state.totalPaymentPrice = computePaymentPrice(state);
                state.totalShipmentPrice = computeShipmentPrice(state);
                state.activeShipmentTresholdDifference =
                    computeActiveShipmentTresholdDifference(state);
            }
        });
    },
}))));
const computeItemsPrice = (state) => {
    const currency = useGlobalStore.getState().config.currency;
    const customerDiscount = useGlobalStore.getState().config.customer?.discount ?? 0;
    let servicesPrice = 0;
    state.activeServices.forEach((item) => {
        item.serviceIds.forEach((id) => {
            const service = state.cart.items
                .find((item) => item.id === item.id)
                ?.services.find((service) => service.id === id);
            if (service)
                servicesPrice += roundPrice(service.price, currency);
        });
    });
    const itemsPrice = state.cart.items.reduce((acc, item) => {
        let discount = item.discount || 0;
        if (customerDiscount > 0) {
            discount = discount > 0 ? discount * (customerDiscount / 100) : customerDiscount;
        }
        return acc + roundPrice(computePriceWithDiscount(item.price, discount), currency) * item.quantity * item.custom_quantity;
        ;
    }, 0);
    return itemsPrice + servicesPrice;
};
const computeItemsPriceWithDiscount = (state) => {
    const currency = useGlobalStore.getState().config.currency;
    return roundPrice((state.totalItemsPrice * (100 - state.couponDiscount)) / 100, currency);
};
const computeItemsWeight = (state) => {
    return state.cart.items.reduce((acc, item) => {
        return (acc += item.weight * item.quantity);
    }, 0);
};
const computePaymentPrice = (state) => {
    const currency = useGlobalStore.getState().config.currency;
    if (!state.activePayment)
        return 0;
    if (state.activePayment.discounts.length === 0)
        return roundPrice(state.activePayment.price, currency);
    return ((roundPrice(state.activePayment.price, currency) *
        (100 -
            roundPrice(state.activePayment.discounts[0].discount, currency))) /
        100);
};
const computeShipmentPrice = (state) => {
    const currency = useGlobalStore.getState().config.currency;
    if (!state.activeShipment)
        return 0;
    return roundPrice(state.activeShipment.finalPrice, currency);
};
const computeActiveShipmentTresholdDifference = (state) => {
    const currency = useGlobalStore.getState().config.currency;
    if (!state.activeShipment)
        return "skip";
    if (state.totalItemsPriceWithDiscount >=
        state.activeShipment.free_shipping_threshold)
        return "skip";
    return roundPrice(state.activeShipment.free_shipping_threshold -
        state.totalItemsPriceWithDiscount, currency);
};
const computeAvailableGifts = (state) => {
    let result = [];
    for (const gift of state.cart.gifts) {
        if (gift.min_order_price > state.totalItemsPriceWithDiscount)
            continue;
        for (const product of gift.products) {
            if (product.quantity > 0 &&
                state.activeGift?.gift.id !== gift.id &&
                state.activeGift?.product.id !== product.id)
                result.push({ gift, product });
        }
    }
    return result;
};
const updateCurrentCartItemsCount = (count) => {
    const cartCount = document.querySelector(CART_COUNT);
    cartCount.innerHTML = count;
};
export const getCart = async (url) => {
    const data = await fetchJson(url);
    return data;
};
export const addCartItem = async (url, productId, quantity, variantId, services) => {
    const response = await fetch(`${url}/${productId}`, {
        method: "POST",
        headers: {
            "X-CSRF-TOKEN": getCsfrToken(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            variant_id: variantId ?? null,
            quantity: quantity,
            services,
        }),
    });
    const result = await response.json();
    updateCurrentCartItemsCount(result.count_cart_items);
};
export const editCartItem = async (url, rowId, id, quantity) => {
    const response = await fetch(`${url}/${rowId}/${id}`, {
        method: "POST",
        headers: {
            "X-CSRF-TOKEN": getCsfrToken(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            quantity: quantity,
        }),
    });
    const result = await response.json();
    updateCurrentCartItemsCount(result.count_cart_items);
};
export const removeCartItem = async (url, rowId, id) => {
    await fetch(`${url}/${rowId}/${id}`, {
        method: "DELETE",
        headers: {
            "X-CSRF-TOKEN": getCsfrToken(),
            "Content-Type": "application/json",
        },
    });
};
export const createOrder = async (url, data) => {
    return fetchJson(`${url}`, {
        method: "POST",
        body: JSON.stringify(data),
    });
};
